// CSV import / export for vault entries.
//
// One header row, then one entry per row. The first column is the entry
// type, which dispatches the per-type field mapping. Empty cells stand in
// for unset fields. Attachments are not represented (CSV can't hold binary).

import { csvParse, csvStringify } from '../util/csv.js';

export const COLUMNS = [
  'type', 'title',
  // login
  'username', 'password', 'url',
  // card
  'cardholder', 'number', 'expiryMonth', 'expiryYear', 'cvv', 'pin',
  // license
  'product', 'version', 'licenseKey', 'orderNumber', 'purchaseDate',
  // identity
  'firstName', 'lastName', 'phone', 'address',
  // apiKey
  'service', 'key', 'secret',
  // note
  'body',
  // common — kept last so login/identity emails fall here
  'email', 'notes',
];

export const SUPPORTED_TYPES = new Set(['login', 'card', 'license', 'identity', 'note', 'apiKey']);

/**
 * Serialize an array of vault entries to a CSV string.
 * @returns {{ csv: string, skippedAttachments: number }}
 */
export function entriesToCsv(entries) {
  let skippedAttachments = 0;
  const rows = [COLUMNS];
  for (const e of entries) {
    if (Array.isArray(e.attachments) && e.attachments.length) {
      skippedAttachments += e.attachments.length;
    }
    rows.push(COLUMNS.map((col) => valueOf(e, col)));
  }
  return { csv: csvStringify(rows), skippedAttachments };
}

function valueOf(entry, col) {
  if (col === 'type') return entry.type ?? '';
  return entry[col] ?? '';
}

/**
 * Parse a CSV string into entries ready for vault.upsert.
 * Tolerates extra columns and reordering as long as `type` and `title` exist.
 */
export function csvToEntries(text) {
  const rows = csvParse(text);
  if (rows.length === 0) return { entries: [], errors: [], summary: { total: 0, byType: {}, skipped: 0 } };

  const header = rows[0].map((h) => h.trim());
  if (!header.includes('type')) {
    throw new Error('CSV is missing a "type" column');
  }
  const colIndex = Object.fromEntries(header.map((h, i) => [h, i]));

  const entries = [];
  const errors = [];
  const byType = {};
  let skipped = 0;

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.length === 1 && row[0] === '') continue; // blank line
    const cell = (name) => (name in colIndex ? (row[colIndex[name]] ?? '') : '');
    const type = (cell('type') || '').trim();
    if (!type) { skipped++; continue; }
    if (!SUPPORTED_TYPES.has(type)) {
      errors.push({ row: r + 1, message: `Unsupported type "${type}"` });
      continue;
    }
    const entry = buildEntry(type, cell);
    if (!entry.title && !hasAnyContent(entry)) { skipped++; continue; }
    entries.push(entry);
    byType[type] = (byType[type] ?? 0) + 1;
  }

  return { entries, errors, summary: { total: rows.length - 1, byType, skipped, errors: errors.length } };
}

function buildEntry(type, cell) {
  const base = { type, title: cell('title') };
  const set = (key, src = key) => { const v = cell(src); if (v) base[key] = v; };
  switch (type) {
    case 'login':
      set('username'); set('password'); set('url'); set('notes');
      break;
    case 'card':
      set('cardholder'); set('number'); set('expiryMonth'); set('expiryYear');
      set('cvv'); set('pin'); set('notes');
      break;
    case 'license':
      set('product'); set('version'); set('licenseKey'); set('email');
      set('orderNumber'); set('purchaseDate'); set('notes');
      break;
    case 'identity':
      set('firstName'); set('lastName'); set('email'); set('phone');
      set('address'); set('notes');
      break;
    case 'apiKey':
      set('service'); set('key'); set('secret'); set('notes');
      break;
    case 'note':
      set('body');
      break;
  }
  return base;
}

function hasAnyContent(entry) {
  for (const [k, v] of Object.entries(entry)) {
    if (k === 'type') continue;
    if (v != null && v !== '') return true;
  }
  return false;
}
